import axios from 'axios'
import { setItem, removeItem, clear } from '@/store/localStorage'
import { account } from '@/store/account.module'
import { setClientToken } from '@/api/api_var'
import { pad2, get, post } from '@/api/Services'
import i18n from '@/i18n'
import router from '@/router'

class AuthService {
  async getUserParam(responseData) {
    const params = {
      sqlId: 25,
      parameters: JSON.stringify([
        {
          parameter_name: 'pin_selected_user_id',
          parameter_value: responseData.id || -1,
        },
      ]),
    }

    const data = await post('Commons/Executer', params)
    if (data) {
      const userParam = data[0]
      setItem('userParam', userParam)
      account.state.userParam = userParam
    }
  }

  async googleAccount(user) {
    try {
      const response = await post('Users/GoogleSignupOrLogin', user)
      // Check if response and response.data are defined
      if (response.status == 0) {
        const { user: userData, token } = response
        await this.getUserParam(userData)
        setClientToken(token)
        setItem('loggeduser', JSON.stringify(userData))
        setItem('rights', JSON.stringify(userData.roleId))

        Object.assign(account.state, {
          loggeduser: userData,
          rights: userData.roleId,
          token,
          emailConfirm: userData.emailConfirmed,
        })

        removeItem('emailConfirm')
        setItem('token', token)

        const isProfilePic = await this.hasProfilePic(userData)
        const { googleProfilePic } = user

        if (googleProfilePic && isProfilePic.status === -1) {
          const googlePic = await this.urlToFile(
            googleProfilePic,
            `${Date.now()}_${userData.email}.png`,
            'image/png'
          )
          if (googlePic != null) {
            await this.setProfilePic(userData, googlePic)
          }
        }

        return response
      } else {
        // Check if response.data is defined and if it contains a message property
        if (response && response.data) {
          throw new Error(response.data[0]?.message || response.data.message)
        } else {
          throw new Error('Unknown error occurred')
        }
      }
    } catch (error) {
      return Promise.reject(error.response?.data?.message || error.message)
    }
  }

  async linkedinAccount(code) {
    try {
      const response = await get('/Users/linkedin-callback?code=' + code)

      if (!response || response.status !== 0) {
        throw new Error(response?.message || 'LinkedIn authentication failed')
      }

      const { user: userData, token } = response
      await this.getUserParam(userData)
      setClientToken(token)
      setItem('loggeduser', JSON.stringify(userData))
      setItem('rights', JSON.stringify(userData.roleId))

      Object.assign(account.state, {
        loggeduser: userData,
        rights: userData.roleId,
        token,
        emailConfirm: userData.emailConfirmed,
      })

      removeItem('emailConfirm')
      setItem('token', token)

      const isProfilePic = await this.hasProfilePic(userData)
      const { linkedinProfilePic } = response

      if (linkedinProfilePic && isProfilePic.status === -1) {
        const linkedinPic = await this.urlToFile(
          linkedinProfilePic,
          `${Date.now()}_${userData.email}.png`,
          'image/png'
        )
        if (linkedinPic != null) {
          await this.setProfilePic(userData, linkedinPic)
        }
      }

      return response
    } catch (error) {
      return Promise.reject(error.response?.data?.message || error.message)
    }
  }

  async setProfilePic(userData, file) {
    const formData = new FormData()
    formData.append('FileTypeId', 1)
    formData.append('ReferenceId', userData.id)
    formData.append('CreateUser', userData.id)
    formData.append('Description', `${userData.email}-google-profile-pic`)
    formData.append('File', file)
    formData.append('IsDefault', 1)

    try {
      await post('/Files/CreateFile', formData)
    } catch (error) {
      throw new Error(error.response?.data?.message || error.message)
    }
  }

  async hasProfilePic(userData) {
    const parameters = {
      FileTypeId: 1,
      ReferenceId: userData.id,
      IsDefault: 1,
    }

    try {
      const response = await post('/Files/GetFilesByReferenceId', parameters)
      return response
    } catch (error) {
      return Promise.reject(error.response?.data?.message || error.message)
    }
  }

  async urlToFile(url, filename, mimeType, retries = 3) {
    try {
      const response = await fetch(url)

      // Check for rate limit
      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After') || 1 // Default to 1 second if Retry-After header is not present
        console.warn(
          `Rate limit exceeded. Retrying after ${retryAfter} seconds...`
        )
        await new Promise((resolve) => setTimeout(resolve, retryAfter * 1000)) // Wait for the specified time
        if (retries > 0) {
          return this.urlToFile(url, filename, mimeType, retries - 1) // Retry the request
        }
        throw new Error('Rate limit exceeded and retry limit reached')
      }

      if (!response.ok) {
        this.$toast.add({
          severity: 'error',
          summary: 'Failed to fetch URL:',
          detail: `${response.statusText}`,
          life: 3000,
        })
        return null
      }

      const blob = await response.blob()
      return new File([blob], filename, { type: mimeType })
    } catch (error) {
      throw new Error(`Failed to convert URL to file: ${error.message}`)
    }
  }

  // async setStringPic(googleProfilePic, userData) {
  //     const formData = new FormData();
  //     formData.append("FileTypeId", 1);
  //     formData.append("ReferenceId", userData.id);
  //     formData.append("Description", `${userData.email}-google-profile-pic`);
  //     formData.append("Name", `${userData.email+'_'+Date.now()}-google-profile-pic`);
  //     formData.append("Path", googleProfilePic);
  //     formData.append("IsDefault", 1);
  //     try {
  //         await post("/Files/CreateExternalFile", formData);
  //     } catch (error) {
  //         throw new Error(error.response?.data?.message || error.message);
  //     }
  // }
  async login(user) {
    try {
      const response = await axios.post('/api/Users/Login', {
        username: user.username,
        password: user.password,
        recaptchaToken: user.recaptchaToken,
      })

      if (response.status === 200 && response.statusText === 'OK') {
        await this.getUserParam(response.data.user)
        setClientToken(response.data.token)
        setItem('loggeduser', JSON.stringify(response.data.user))
        setItem('rights', JSON.stringify(response.data.user.roleId))

        Object.assign(account.state, {
          loggeduser: response.data.user,
          rights: response.data.user.roleId,
          token: response.data.token,
          emailConfirm: response.data.emailConfirmed,
        })

        removeItem('emailConfirm')
        setItem('token', response.data.token)

        return response.data
      } else {
        throw new Error(response.data[0]?.message)
      }
    } catch (error) {
      return Promise.reject(error.response?.data?.message || error.message)
    }
  }

  encode(input) {
    const keyStr =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/='
    let output = ''
    let i = 0

    while (i < input.length) {
      const chr1 = input.charCodeAt(i++)
      const chr2 = input.charCodeAt(i++)
      const chr3 = input.charCodeAt(i++)

      const enc1 = chr1 >> 2
      const enc2 = ((chr1 & 3) << 4) | (chr2 >> 4)
      const enc3 = ((chr2 & 15) << 2) | (chr3 >> 6)
      const enc4 = chr3 & 63

      output +=
        keyStr.charAt(enc1) +
        keyStr.charAt(enc2) +
        keyStr.charAt(isNaN(chr2) ? 64 : enc3) +
        keyStr.charAt(isNaN(chr3) ? 64 : enc4)
    }

    return output
  }

  async getActiveUsers() {
    try {
      const response = await get('/activeusers')
      if (response.data) {
        const activeUsers = response.data.length
        setItem('sessionCount', activeUsers)
        return response
      } else {
        throw new Error(
          'The username or password is incorrect, please try again.'
        )
      }
    } catch (error) {
      throw new Error(
        error.message ||
          'The username or password is incorrect, please try again.'
      )
    }
  }

  logout() {
    Object.assign(account.state, {
      loggeduser: null,
      token: null,
      userParam: null,
      rights: null,
      dashboard: null,
      userMenu: null,
      status: null,
    })
    router.push({ name: 'home' })
  }

  async register(user) {
    const formData = new FormData()
    formData.append('Firstname', user.firstname)
    formData.append('Lastname', user.lastname)
    formData.append('LinkedinPage', user.linkedin)
    formData.append('Email', user.email)
    formData.append('Password', user.password)
    formData.append('AccountUseIbanOrAccnum', '1')
    formData.append('ProfilePictureFile', '')
    formData.append('RoleId', user.roleId)
    if (user.phone) formData.append('Phone', user.phone)

    const currentDate = new Date()
    const dateNowDay = `${currentDate.getFullYear()}${pad2(
      currentDate.getMonth() + 1
    )}${pad2(currentDate.getDate())}`
    const dateNowTime = `${pad2(currentDate.getHours())}${pad2(
      currentDate.getMinutes()
    )}`
    const hash1 = eval(process.env.VUE_APP_KEY)

    axios.defaults.headers.post = {
      'Content-Type': 'multipart/form-data',
      'Access-Control-Allow-Origin': '*',
      Authorization:
        'Basic ' + this.encode(user.username + ':' + user.password),
    }
    axios.defaults.headers.get['Key'] = hash1

    try {
      const response = await axios.post('/api/Users', formData)
      if (response.status === 200) {
        return response.data
      } else {
        throw new Error(
          'The username or password is incorrect, please try again.'
        )
      }
    } catch (error) {
      throw new Error(i18n.t('signupError'))
    }
  }
}

export default new AuthService()
